import { FormDataFrontDeskProps } from 'components/modals/AddFrontDeskTicket/addFrontDeskTicket.types';
import {
  CareChannelListProps,
  ChannelProps as ContextChannelProps
} from 'contexts/MessagesContext/messagesContext.types';

import { apiSlice } from '../api/apiSlice';
import { ChannelProps, FrontDeskChannelProps } from './channel.types';

export const channelsApiSlice = apiSlice.injectEndpoints({
  endpoints: (build) => ({
    createChannel: build.mutation<ChannelProps, { channelTitle: string; patientId: string }>({
      invalidatesTags: ['RespondState'],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data: patientDetail } = await queryFulfilled;
          const patientId = args.patientId;

          const patientDetailTransformed = {
            channelDescription: patientDetail.channelDescription,
            channelId: patientDetail._id,
            channelTitle: patientDetail.channelTitle,
            isCompleted: patientDetail.isCompleted,
            isResponded: patientDetail.isResponded,
            patientId,
            unreadMessageCount: 0
          };

          dispatch(
            channelsApiSlice.util.upsertQueryData('getPatientChannels', { patientId }, [
              patientDetailTransformed
            ])
          );
        } catch (error) {
          console.error(error);
        }
      },
      query: ({ channelTitle, patientId }) => ({
        body: { channelTitle, patientId },
        method: 'POST',
        url: '/channels'
      }),
      transformResponse: (response: { data: ChannelProps }) => response.data
    }),
    createFrontDeskChannel: build.mutation({
      invalidatesTags: [{ id: 'LIST', type: 'FrontDeskChannel' }, 'RespondState'],
      query: (body) => ({
        body,
        method: 'POST',
        url: '/front-desk-channels'
      }),
      transformResponse: (response: { data: FormDataFrontDeskProps }) => response.data
    }),
    getFrontDeskChannels: build.query<CareChannelListProps[], { patientId: string }>({
      providesTags: (result) =>
        result
          ? [
              { id: 'LIST', type: 'FrontDeskChannel' },
              ...result.map(({ channelId }) => ({
                id: channelId,
                type: 'FrontDeskChannel' as const
              }))
            ]
          : ['FrontDeskChannel'],
      query: ({ patientId }) => ({
        params: { fetchFirstMessage: false },
        url: `/channels/${patientId}/frontdesk-channels`
      }),
      transformResponse: (response: {
        data: FrontDeskChannelProps[];
        info: { totalCount: number };
      }) =>
        response.data?.map((item) => ({
          _id: item.channelId,
          channelDescription: item.frontDeskRequestType,
          channelId: item.channelId,
          channelTitle: item.frontDeskRequestType,
          isCompleted: item.isCompleted,
          isResponded: item.isResponded,
          latestMessage: item.latestMessage,
          latestMessageDate: item.latestMessageDate,
          patientId: item.patientDetail.id,
          unreadMessageCount: item.unreadMessageCount
        })) || []
    }),
    getPatientChannels: build.query<CareChannelListProps[], { patientId: string }>({
      query: ({ patientId }) => `/channels/${patientId}/patient-channels`,
      transformResponse: (response: { data?: ContextChannelProps[] }) =>
        response.data?.map((item) => ({
          channelDescription: item.channelDescription,
          channelId: item.channelId,
          channelTitle: item.channelTitle,
          isCompleted: item.isCompleted,
          isResponded: item.isResponded,
          latestMessage: item.latestMessage,
          latestMessageDate: item.latestMessageDate,
          patientId: item.patientId,
          unreadMessageCount: item.unreadMessageCount
        })) || []
    }),
    getRespondState: build.query<{ isResponded: boolean }, { patientId: string }>({
      providesTags: ['RespondState'],
      query: ({ patientId }) => ({
        url: `/channels/${patientId}/respond-state`
      }),
      transformResponse: (response: { data: { isResponded: boolean } }) => response.data
    })
  })
});

export const {
  useCreateChannelMutation,
  useCreateFrontDeskChannelMutation,
  useGetPatientChannelsQuery,
  useGetRespondStateQuery,
  useLazyGetFrontDeskChannelsQuery,
  useLazyGetPatientChannelsQuery
} = channelsApiSlice;
